import clsx from 'clsx'
import { ArrowRightIcon } from 'lucide-react'
import React, { useMemo } from 'react'

import { membersMarketplace } from 'rails_routes'
import { ButtonLink, useBreakpoint } from 'slab'
import { useGetMarketplaceProducts } from 'utils/web_api'
import { type MarketplaceProduct } from 'utils/web_api.schemas'

import { type MarketplaceProductCategory } from '../../../marketplace/shared/types'
import { SkeletonItem } from '../../../reusable/SkeletonItem'
import MarketplaceProductBadge from '../MarketplaceProductBadge/MarketplaceProductBadge'

interface ExploreMarketplaceCardProps {
  nextToEnrollmentCardWithFinancialStatus?: boolean;
  filterToCategories?: string[];
  data?: MarketplaceProduct[];
  isLoading?: boolean;
}

/**
 * Picks a random product from a list of products.
 *
 * @param products - The list of products to pick a random product from
 * @returns A randomly selected product, or null if no products are provided
 */
export const pickRandomProduct = (
  products: MarketplaceProduct[]
): MarketplaceProduct | null => {
  if (!products || products.length === 0) return null
  const randomIndex = Math.floor(Math.random() * products.length)
  return products[randomIndex]
}

/**
 * Filters marketplace products based on categories.
 *
 * Only products that explicitly match the categories are returned; if a product
 * has any category not in the filterToCategories, it is not returned.
 *
 * @param originalProducts - The original list of products to filter
 * @param filterToCategories - The categories to filter products appropriately
 * @returns A list of products that match the categories, or empty list if no products match
 */
export const getFilteredProducts = (
  originalProducts: MarketplaceProduct[] | undefined,
  filterToCategories?: string[]
): MarketplaceProduct[] => {
  // Return empty list if no products are available
  if (!originalProducts || originalProducts.length === 0) return []

  const products = []
  for (const product of originalProducts) {
    if (filterToCategories) {
      if (product.categories.length > 0) {
        // Skip products that have categories outside of acceptable categories
        if (product.categories.some(category => !filterToCategories.includes(category))) {
          continue
        }

        products.push(product)
      }
    } else {
      products.push(product)
    }
  }

  return products
}

const ExploreMarketplaceCard: React.FC<ExploreMarketplaceCardProps> = ({
  nextToEnrollmentCardWithFinancialStatus = false,
  filterToCategories,
  data: providedData,
  isLoading: providedIsLoading = false,
}) => {
  const { breakpointIsAtLeast } = useBreakpoint()

  const shouldFetch = providedData === undefined

  const { data: hookData, isLoading: hookIsLoading } = useGetMarketplaceProducts(undefined, {
    query: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: shouldFetch, // Only fetch if data prop is not provided
    }
  })

  // Use provided data if available, otherwise use hook data
  const actualData = shouldFetch ? hookData?.data : providedData
  const actualIsLoading = shouldFetch ? hookIsLoading : providedIsLoading

  const filteredProducts = useMemo(() => {
    return getFilteredProducts(actualData, filterToCategories)
  }, [actualData, filterToCategories])

  const product = useMemo(() => {
    return pickRandomProduct(filteredProducts)
  }, [filteredProducts])

  const renderHighlightedProduct = useMemo(() => {
    if (actualIsLoading) {
      return (
        <div className='flex gap-3'>
          <SkeletonItem heightClass='h-[5rem]' widthClass='w-[6.25rem]' />
          <div className='flex flex-col gap-2'>
            <SkeletonItem heightClass='h-6' widthClass='w-16' />
            <div className='w-40'>
              <SkeletonItem heightClass='h-3' widthClass='w-20' />
              <SkeletonItem heightClass='h-5' widthClass='w-full' />
            </div>
          </div>
        </div>
      )
    }

    if (product) {
      return (
        <a href={membersMarketplace.show({ product_slug: product.slug })}>
          <div className='flex gap-3'>
            {product.image_url && (
              <div className='w-[6.25rem] h-[5rem] shrink-0 bg-white rounded-lg'>
                <img
                  src={product.image_url}
                  alt={`Picture of ${product.name}`}
                  className='rounded-lg object-cover w-full h-full'
                />
              </div>
            )}
            <div className='flex flex-col gap-2'>
              <MarketplaceProductBadge category={product.categories[0] as MarketplaceProductCategory} />
              <div>
                <h3 className='caption text-main text-gray-800'>{product.product_brand}</h3>
                <p className='body-bold text-main text-gray-800'>{product.name}</p>
              </div>
            </div>
          </div>
        </a>
      )
    }
  }, [actualIsLoading, product])

  // If we are next to the enrollment card with a progress tracker, we need additional classes
  // to account for the extra padding & height difference.
  const isAtLeastLg = breakpointIsAtLeast('lg')
  const showImage = product && product.image_url
  const nextToTrackerClass = nextToEnrollmentCardWithFinancialStatus ? 'pt-0 px-4 pb-3 mt-0' : 'border-t border-gray-200 p-4 mt-4'
  const buttonContainerClasses = isAtLeastLg ? `flex justify-end ${nextToTrackerClass}` : 'w-full flex p-4'
  const buttonWidth = isAtLeastLg ? undefined : 'full'

  return (
    <div className='w-full h-full rounded-2xl border border-gray-200 bg-gradient-to-b from-thatch-200 via-thatch-100 to-transparent shadow-sm flex flex-col justify-between'>
      <div className={clsx('flex flex-col px-4', {
        'gap-3': !nextToEnrollmentCardWithFinancialStatus,
        'gap-2': nextToEnrollmentCardWithFinancialStatus,
        'pt-3': nextToEnrollmentCardWithFinancialStatus && showImage,
        'pt-4': !(nextToEnrollmentCardWithFinancialStatus && showImage)
      })}
      >
        <p className='body text-main'>
          Shop high-impact health products & services using your Thatch balance
        </p>
        {renderHighlightedProduct}
      </div>
      <div className={buttonContainerClasses}>
        <ButtonLink
          href={membersMarketplace.index()}
          rightIcon={ArrowRightIcon}
          variant='secondary'
          size='large'
          width={buttonWidth}
        >
          Explore marketplace
        </ButtonLink>
      </div>
    </div>
  )
}

export default ExploreMarketplaceCard
